'use client'

import React from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Progress } from '../ui/progress'
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar'
import { 
  MoreVertical, 
  Eye, 
  Edit, 
  Users, 
  ExternalLink,
  Calendar,
  TrendingUp
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import { Brand } from '../../utils/supabase/client'

interface BrandCardProps {
  brand: Brand & {
    member_count?: number
    recent_activity?: string
  }
  onView: (brand: Brand) => void
  onEdit: (brand: Brand) => void
  onInvite: (brand: Brand) => void
  onViewPublic: (brand: Brand) => void
  onDelete: (brand: Brand) => void
}

export function BrandCard({ 
  brand, 
  onView, 
  onEdit, 
  onInvite, 
  onViewPublic, 
  onDelete 
}: BrandCardProps) {
  // Ensure we have a valid progress value
  const progress = typeof brand.overall_progress === 'number' ? brand.overall_progress : 0

  const getProgressColor = (progress: number) => {
    if (progress < 25) return 'bg-red-500 hover:bg-red-600'
    if (progress < 50) return 'bg-yellow-500 hover:bg-yellow-600'
    if (progress < 75) return 'bg-blue-500 hover:bg-blue-600'
    return 'bg-green-500 hover:bg-green-600'
  }

  const getProgressLabel = (progress: number) => {
    if (progress < 25) return 'Getting Started'
    if (progress < 50) return 'In Progress'
    if (progress < 75) return 'Making Progress'
    return 'Nearly Complete'
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = Math.abs(now.getTime() - date.getTime()) / (1000 * 60 * 60)
    
    if (diffInHours < 24) {
      return 'Today'
    } else if (diffInHours < 48) {
      return 'Yesterday'
    } else {
      return date.toLocaleDateString()
    }
  }

  return (
    <Card className="group hover:shadow-lg transition-all duration-200 border-0 shadow-md">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <Avatar className="h-12 w-12">
              <AvatarImage src="/placeholder-brand-logo.png" alt={brand.name} />
              <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-500 text-white font-semibold">
                {brand.name.substring(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <CardTitle className="text-lg truncate">{brand.name}</CardTitle>
              <CardDescription className="text-sm">
                {brand.description || 'No description'}
              </CardDescription>
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm" 
                className="opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onView(brand)}>
                <Eye className="h-4 w-4 mr-2" />
                View Dashboard
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onEdit(brand)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit Brand
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onInvite(brand)}>
                <Users className="h-4 w-4 mr-2" />
                Invite Team
              </DropdownMenuItem>
              {brand.is_public && (
                <DropdownMenuItem onClick={() => onViewPublic(brand)}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View Public Page
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={() => onDelete(brand)}
                className="text-red-600 focus:text-red-600"
              >
                Delete Brand
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Progress Section */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-600">Progress</span>
            <div className="flex items-center space-x-2">
              <div className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium text-white ${getProgressColor(progress)}`}>
                {getProgressLabel(progress)}
              </div>
              <span className="text-sm font-medium text-slate-700">{progress}%</span>
            </div>
          </div>
          <Progress 
            value={progress} 
            className="h-2"
          />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="flex items-center space-x-2">
            <Users className="h-4 w-4 text-slate-400" />
            <span className="text-slate-600">
              {brand.member_count || 1} member{(brand.member_count || 1) !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <Calendar className="h-4 w-4 text-slate-400" />
            <span className="text-slate-600">
              {formatDate(brand.last_activity)}
            </span>
          </div>
        </div>

        {/* Website URL */}
        {brand.website_url && (
          <div className="text-sm">
            <a 
              href={brand.website_url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 flex items-center space-x-1 truncate"
            >
              <ExternalLink className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">{brand.website_url}</span>
            </a>
          </div>
        )}

        {/* Actions */}
        <div className="flex space-x-2 pt-2">
          <Button 
            onClick={() => onView(brand)}
            className="flex-1"
            size="sm"
          >
            <TrendingUp className="h-4 w-4 mr-2" />
            Continue Building
          </Button>
          {brand.is_public && (
            <Button 
              onClick={() => onViewPublic(brand)}
              variant="outline"
              size="sm"
            >
              <ExternalLink className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}